'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive Field
 * ROLE: none on Field (group-without-fieldset); inner control owns its native role.
 * ARIA: label-for/id via useId; aria-describedby (description + error in DOM order);
 *       aria-invalid; aria-required.
 * KEYBOARD: native browser tab/typing.
 * FOCUS: when error appears via async validation, focus moves to the field's input
 *        (handled by parent <Form> wrapper via id lookup).
 * LIVE-REGION: Field.Error has role="alert" + aria-live="polite".
 * PRM: no internal motion.
 * FORCED-COLORS: standard.
 * RTL: logical properties for spacing.
 * RSC: CLIENT (state tracking for ids + context).
 * WAI-APG: Form Validation pattern.
 * SUBSTRATE: @radix-ui/react-label (FormLabel — internal).
 */
import { Children, cloneElement, isValidElement, useId, } from 'react';
import { Caption } from '../../../server/typography/Caption/Caption';
import { FormLabel } from '../../_internal/FormLabel/FormLabel';
import { FieldContext } from './FieldContext';
import { FieldControl } from './FieldControl';
import { FieldDescription } from './FieldDescription';
import { FieldError } from './FieldError';
import { FieldLabel } from './FieldLabel';
import styles from './Field.module.css';
function FieldInner({ label, description, error, required = false, layout = 'stacked', children, style, }) {
    const baseId = useId();
    const fieldId = `${baseId}-field`;
    const descriptionId = description != null ? `${baseId}-desc` : undefined;
    // Only wire error id when error is non-empty (string/node that is truthy)
    const hasError = error != null && error !== false && error !== '';
    const errorId = hasError ? `${baseId}-err` : undefined;
    const describedByParts = [descriptionId, errorId].filter(Boolean);
    const describedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;
    // In prop-driven mode (not slot decomposition), inject ARIA onto the single
    // child control automatically.
    let control = children;
    if (children != null) {
        const childArray = Children.toArray(children);
        // Only inject when there is exactly one child and it is not already a
        // Field.* compound (those handle their own wiring via context).
        if (childArray.length === 1) {
            const only = childArray[0];
            if (isValidElement(only)) {
                const el = only;
                // Don't double-inject onto Field compound members — they read context
                const displayName = el.type?.displayName;
                const isCompound = displayName?.startsWith('Field.');
                if (!isCompound) {
                    control = cloneElement(el, {
                        id: fieldId,
                        ...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {}),
                        ...(hasError ? { 'aria-invalid': true } : {}),
                        ...(required ? { 'aria-required': true } : {}),
                    });
                }
            }
        }
    }
    const ctxValue = {
        fieldId,
        descriptionId,
        errorId,
        hasError,
        required,
    };
    const labelEl = label != null ? (_jsx(FormLabel, { htmlFor: fieldId, required: required, children: label })) : null;
    const descEl = description != null ? (_jsx(Caption, { id: descriptionId, children: description })) : null;
    const errorEl = hasError ? (_jsx("span", { id: errorId, role: "alert", "aria-live": "polite", style: { font: 'var(--type-caption)', color: 'var(--destructive)' }, children: error })) : null;
    return (_jsx(FieldContext.Provider, { value: ctxValue, children: _jsxs("div", { className: layout === 'inline' ? styles.inline : styles.stacked, style: style, children: [layout === 'stacked' && labelEl, control, layout === 'inline' && labelEl, descEl, errorEl] }) }));
}
/**
 * Single-control field wrapper. Wires label, description, and error to the
 * inner control via id/aria attributes.
 *
 * Standard use (prop-driven):
 * ```tsx
 * <Field label="Email" description="We'll never share it." error={errors.email} required>
 *   <Input type="email" name="email" />
 * </Field>
 * ```
 *
 * Slot decomposition (10% case):
 * ```tsx
 * <Field>
 *   <Field.Label>Email <em>(required)</em></Field.Label>
 *   <Field.Control><Input type="email" /></Field.Control>
 *   <Field.Description>We'll never share it.</Field.Description>
 *   <Field.Error>{errors.email}</Field.Error>
 * </Field>
 * ```
 */
export const Field = Object.assign(FieldInner, {
    Label: Object.assign(FieldLabel, { displayName: 'Field.Label' }),
    Description: Object.assign(FieldDescription, { displayName: 'Field.Description' }),
    Error: Object.assign(FieldError, { displayName: 'Field.Error' }),
    Control: Object.assign(FieldControl, { displayName: 'Field.Control' }),
});
//# sourceMappingURL=Field.js.map